import React from 'react';
import { useNavigate, useSubmit, useActionData, useLoaderData, redirect } from 'react-router-dom';
import { AuthProvider } from '../../auth_provider';
import { ContentFrame } from '../../components/content_frame/content_frame';
import { getModels, getModel, setUrlParameters, getCpfText, setInputCursor } from '../../utils/functions';
import { DESKTOP_PATH, PATIENT_LIST_PATH, PATIENT_ADD_PATH, PATIENT_EDIT_PATH, LOGIN_PATH } from '../../constants/paths';
import { CLINICS_GET_API, PATIENT_GET_API } from '../../constants/apis';
import { PatientData } from './patient_data';


const DEFAULT_PATIENT = {
  clinic_id: "",
  name: "",
  cpf: "",
  notes: "",
  is_active: true
};

export async function patientAddLoader() {
  try {
    let clinics = await getModels(setUrlParameters(CLINICS_GET_API, {is_active: true}));
    let patient = DEFAULT_PATIENT;

    clinics.sort((a, b) => a.name.localeCompare(b.name));

    return {clinics, patient};
  }
  catch(errors) {
    if(errors instanceof Array) {
      for(let error of errors) {
        if(error.code === 209) {
          // Expired user session
          return redirect(LOGIN_PATH);
        }
        else if(error.code === 203) {
          // Not authorized
          return redirect(DESKTOP_PATH);
        }
      }
    }

    throw errors;    
  }
};

export async function patientEditLoader({ params }) {
  try {
    let clinics;
    let patient;

    if (AuthProvider.is_authenticated) {
      clinics = getModels(CLINICS_GET_API);
      patient = getModel(`${PATIENT_GET_API}${params.patient_id}`);
      
      clinics = await clinics;
      patient = await patient;
    }
    else {
      clinics = await getModels(CLINICS_GET_API);
      patient = await getModel(`${PATIENT_GET_API}${params.patient_id}`);
    }

    clinics.sort((a, b) => a.name.localeCompare(b.name));

    return {clinics, patient};
  }
  catch(errors) {
    if(errors instanceof Array) {
      for(let error of errors) {
        if(error.code === 209) {
          // Expired user session
          return redirect(LOGIN_PATH);
        }
        else if(error.code === 203) {
          // Not authorized
          return redirect(DESKTOP_PATH);
        }
      }
    }

    throw errors;    
  }
};


class Patient extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      patient: {
        ...this.props.patient
      }
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.patient !== this.props.patient) {
      this.setState({
        patient: {
          ...this.props.patient
        }
      });
    }
  }

  handleInputChange(event) {
    const target = event.target;
    let value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;

    if(value && name === 'cpf') {
      const initialPosition = target.selectionStart;
      const initialSize = value.length;

      value = value.replace(/\D/g,"");
      target.value = getCpfText(value);

      let newPosition = (target.value.length - initialSize) + initialPosition;

      if(initialPosition < initialSize) {
        newPosition = initialPosition;
      }
      
      const currentValue = this.state.patient[name] || '';

      const valueIncreased = currentValue.length < value.length;

      if(value.length > 3 &&
          (newPosition === 4)) {
        newPosition = valueIncreased ? 5 : 3;
      }
      else if(value.length > 6 &&
          (newPosition === 8)) {
        newPosition = valueIncreased ? 9 : 7;
      }
      else if(value.length > 9 &&
          (newPosition === 12)) {
        newPosition = valueIncreased ? 13 : 11;
      }

      setInputCursor(target, newPosition);
    }

    const newData = {...this.state.patient, [name]: value};

    this.setState({
      patient: newData
    });
  }

  inputsAreValid() {    
    return this.state.patient.name.length > 0 &&
           this.state.patient.clinic_id && this.state.patient.clinic_id > 0;
  }

  clearWarningMessage() {
    this.props.submit(
      {resetWarnings: true}, 
      {
        method: 'POST',
        encType: "application/json"
      });
  }

  async saveData() {
    let data = {...this.state.patient};
    
    this.props.submit(
      {data}, 
      {
        method: this.props.isEdit ? 'PATCH' : 'POST',
        encType: "application/json"
      });
  }

  render() {
    return (
      <ContentFrame
        headerHistory={[
          {
            path: DESKTOP_PATH,
            text: "Área de trabalho"
          },
          {
            path: PATIENT_LIST_PATH,
            text: "Listar pacientes"
          },
          {
            path: this.props.isEdit ? PATIENT_EDIT_PATH : PATIENT_ADD_PATH,
            text: this.props.isEdit ? 'Editar paciente' : 'Adicionar paciente'
          },
        ]}
        titleIcon={this.props.isEdit ? <i className="fas fa-edit"></i> : <i className="fas fa-plus"></i>}
        title={this.props.isEdit ? 'Editar paciente' : 'Adicionar paciente'}
      >

        <PatientData
          warningMessage={this.props.warningMessage}
          showWarningMessage={this.props.warningMessage.length > 0}
          clinics={this.props.clinics}
          patient={this.state.patient}
          onSave={() => this.saveData()}
          onCloseWarning={() => this.clearWarningMessage()}
          enableSave={this.inputsAreValid()}
          handleInputChange={(event) => this.handleInputChange(event)}
          highlights={this.props.parametersToHighlight}
          onCancelPath={PATIENT_LIST_PATH}
          isEdit={this.props.isEdit}
        />

      </ContentFrame>
    );
  }
};

const WrappedPatient = props => {
  const {clinics, patient} = useLoaderData();
  const navigate = useNavigate();
  const submit = useSubmit();
  const actionData = useActionData();

  let warningMessage = '';
  let parametersToHighlight = [];

  if (actionData) {
    warningMessage = actionData.message || '';
    parametersToHighlight = actionData.parameters || [];
  }  

  return (
    <Patient
      navigate={navigate}
      submit={submit}
      warningMessage={warningMessage}
      parametersToHighlight={parametersToHighlight}
      clinics={clinics}
      patient={patient}
      {...props}
    />
  );
};

export { WrappedPatient as Patient};
